import { getStroke } from "perfect-freehand";

/** Variable-width ("pressure") ink geometry, shared by the live Fabric editor
 * and the read-only SVG overlay so a stroke renders identically in both.
 *
 * A stroke is stored as page-relative [0,1] points, each optionally carrying a
 * stylus `pressure` in [0,1]. When pressure is present we render a filled
 * outline (perfect-freehand) whose width follows pressure; otherwise the caller
 * draws a plain uniform polyline (the original, zero-risk path). */

export interface InkPoint {
  x: number;
  y: number;
  pressure?: number | null;
}

/** Per-stroke smoothing preference (plan §3.7): how aggressively perfect-freehand
 * filters the raw pen input when deriving a pressure outline. The preset is a
 * SESSION preference (persisted with the tool prefs, annotation-tool store) —
 * a saved annotation stores only its raw sample points, never the preset. */
export type SmoothingPreset = "precise" | "balanced" | "smooth";

/** The perfect-freehand params each preset maps to. Chosen values:
 *  - `balanced`  — the exact params this module always hard-coded (streamline .5,
 *                  smoothing .5): the default, and what the read-only overlay and
 *                  reload path render with, so existing ink is pixel-identical.
 *  - `precise`   — low streamline/smoothing: the outline hugs the raw samples for
 *                  technical writing (annotations, formulas) where every micro
 *                  direction change is deliberate.
 *  - `smooth`    — high streamline/smoothing: heavy input filtering for lazy,
 *                  flowing ink (cursive, sketching) that irons out hand jitter.
 * `thinning` (pressure → width response) is deliberately identical across presets:
 * how hard the pen was pressed is signal, not jitter — smoothing must never mute it. */
export const SMOOTHING_PRESETS: Record<
  SmoothingPreset,
  { streamline: number; smoothing: number; thinning: number }
> = {
  precise: { streamline: 0.2, smoothing: 0.25, thinning: 0.6 },
  balanced: { streamline: 0.5, smoothing: 0.5, thinning: 0.6 },
  smooth: { streamline: 0.75, smoothing: 0.8, thinning: 0.6 },
};

/** True when the stroke carries real per-point pressure → render variable
 * width. Old/mouse strokes have no pressure and stay uniform polylines. */
export function hasPressure(
  points: ReadonlyArray<InkPoint> | null | undefined,
): boolean {
  return !!points && points.some((p) => p.pressure != null);
}

/** perfect-freehand outline in the SAME coordinate space as the input points
 * (absolute px). `sizePx` is the nominal stroke width. A single point yields a
 * round dot (perfect-freehand duplicates it internally) so a deliberate tap
 * inks; returns [] only for an empty stroke. The input samples are never
 * mutated or resampled — they remain the durable truth a resize/reload
 * re-derives the outline from. `preset` picks the smoothing params (§3.7);
 * the default keeps the reload/read-overlay path byte-identical to before. */
export function strokeOutline(
  points: ReadonlyArray<InkPoint>,
  sizePx: number,
  preset: SmoothingPreset = "balanced",
): number[][] {
  if (!points || points.length < 1) return [];
  const input = points.map(
    (p) => [p.x, p.y, p.pressure ?? 0.5] as [number, number, number],
  );
  const params = SMOOTHING_PRESETS[preset] ?? SMOOTHING_PRESETS.balanced;
  return getStroke(input, {
    size: Math.max(1, sizePx),
    thinning: params.thinning,
    smoothing: params.smoothing,
    streamline: params.streamline,
    // We supply real pressure; never fabricate it from velocity. Missing
    // pressure (mouse) falls back to a constant 0.5 → uniform width.
    simulatePressure: false,
    last: true,
  }) as number[][];
}

/** A smooth SVG path `d` through `pts` built from a quadratic Bézier between
 * successive point MIDPOINTS, with each raw sample as the curve's control point
 * (the classic cheap "smooth freehand" technique — the same one perfect-freehand
 * recommends for rendering). This is the fix for choppy ink (Bug C): a fast
 * stylus drag samples sparsely, so joining raw samples with straight `L`
 * segments reads as an angular polyline whose round caps/joins leave triangular
 * "negative-space" gaps — worse the more you zoom in, since each segment is
 * magnified. Routing the curve through midpoints rounds every corner at render
 * time. It is pure geometry, O(points), and completely INDEPENDENT of the input
 * decimation ("smoothing level") preset — which is why changing that preset
 * never fixed the choppiness.
 *
 * `closed` closes the path with `Z` (for a FILLED perfect-freehand outline);
 * open (the default) leaves it as a stroked centre-line (round caps close the
 * ends). Degenerate inputs fall back to a moveto / a straight line. */
export function strokePath(pts: ReadonlyArray<ArrayLike<number>>, closed = false): string {
  const n = pts.length;
  if (n === 0) return "";
  const x = (i: number) => pts[i]![0] ?? 0;
  const y = (i: number) => pts[i]![1] ?? 0;
  if (n === 1) return `M ${x(0)} ${y(0)}`;
  if (n === 2) return `M ${x(0)} ${y(0)} L ${x(1)} ${y(1)}${closed ? " Z" : ""}`;
  const avg = (a: number, b: number) => (a + b) / 2;
  // First span: a real quadratic from p0, control p1, ending at mid(p1, p2).
  let d = `M ${x(0)} ${y(0)} Q ${x(1)} ${y(1)} ${avg(x(1), x(2))} ${avg(y(1), y(2))}`;
  // Chain smooth quadratics (`T` reflects the prior control point) through the
  // remaining midpoints, so every interior sample rounds its corner.
  for (let i = 2; i < n - 1; i++) {
    d += ` T ${avg(x(i), x(i + 1))} ${avg(y(i), y(i + 1))}`;
  }
  // Anchor the final point exactly (the last midpoint would otherwise stop short).
  d += ` T ${x(n - 1)} ${y(n - 1)}`;
  return closed ? d + " Z" : d;
}

/** A closed SVG path `d` for a perfect-freehand outline polygon. Smoothed with
 * {@link strokePath} so even the outline's own vertices read as a flowing edge.
 * Returns "" for a degenerate (<3-vertex) outline, matching the old contract. */
export function outlineToSvgPath(outline: ReadonlyArray<number[]>): string {
  if (outline.length < 3) return "";
  return strokePath(outline, true);
}

/** Trace `pts` as a smooth quadratic path into a 2D canvas context — the live
 * (wet-ink) twin of {@link strokePath}, using the same midpoint technique so the
 * on-screen preview matches the saved render and never looks choppy on a fast
 * drag. Builds the path only; the caller sets the style and calls stroke()/fill().
 * A begin/closePath is the caller's responsibility. */
export function traceSmooth(
  ctx: CanvasRenderingContext2D,
  pts: ReadonlyArray<{ x: number; y: number }>,
): void {
  const n = pts.length;
  if (n === 0) return;
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  if (n === 1) return;
  if (n === 2) {
    ctx.lineTo(pts[1]!.x, pts[1]!.y);
    return;
  }
  let i = 1;
  for (; i < n - 2; i++) {
    ctx.quadraticCurveTo(
      pts[i]!.x,
      pts[i]!.y,
      (pts[i]!.x + pts[i + 1]!.x) / 2,
      (pts[i]!.y + pts[i + 1]!.y) / 2,
    );
  }
  // Final span: control = penultimate sample, end = last sample.
  ctx.quadraticCurveTo(pts[i]!.x, pts[i]!.y, pts[i + 1]!.x, pts[i + 1]!.y);
}
